import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, ButtonLink, TextBox, Overlay } from "../..";
import { Form, Formik } from "formik";

// * Styles
import "../../../styles/login.scss";

// * Images
import { ReactComponent as ArrowRightSvg } from "../../../assets/icons/arrow-right.svg";
import { ReactComponent as ArrowRightDisabledSvg } from "../../../assets/icons/arrow-right-disabled.svg";
import { ReactComponent as EmailSvg } from "../../../assets/icons/email.svg";
import useAuth from "../../../hooks/useAuth";
import AppContext from "../../../AppContext";
import { langComponent } from "../../../constants";

const ForgotPassword = (props) => {
  const [requested, setRequested] = useState(false);
  const [resent, setResent] = useState(true);

  // * Context
  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.auth);
  const TranslationCommon = getLang(langComponent.common);

  // * Props
  const { email, onClose, onReturn } = props;
  const { createPasswordReset, checkEmailExistence } = useAuth();

  // * Mount
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDownEvent, false);

    return () => {
      document.removeEventListener("keydown", handleKeyDownEvent, false);
    };
  }, []);

  const handleKeyDownEvent = (e) => {
    if ((e.key ?? "").toString().toLowerCase() !== "escape") return;
    handleClose();
  };

  // * Handle closing window
  const handleClose = () => onClose();

  // * Handle return
  const handleReturn = () => onReturn();

  // * Handle submit
  const handleSubmit = ({ email }, { setSubmitting, setFieldError }) => {
    checkEmailExistence(email).then((result) => {
      if (!result.status) {
        setFieldError("email", Translation("Account does not exist"));
        setSubmitting(false);
        return;
      }

      createPasswordReset(email)
        .then(() => {
          setRequested(true);
        })
        .catch(() => {
          setSubmitting(false);
        });
    });
  };

  return (
    <Overlay
      onClose={handleClose}
      content={
        <div className="forgot-password">
          <h1 className="forgot-password-header">
            {Translation("Forgot Password")}
          </h1>
          <Formik
            initialValues={{ email }}
            validate={({ email }) => {
              let errors = {};

              if (!email) errors.email = Translation("Please specify an email");

              if (email) {
                // * Check if email is valid
                const validEmaiLRegex =
                  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

                if (!email.match(validEmaiLRegex)) {
                  errors.email = Translation("Invalid email");
                }
              }

              return errors;
            }}
            onSubmit={handleSubmit}
          >
            {({ values, isValid }) => {
              return (
                <Form className="forgot-password-form">
                  <TextBox
                    name="email"
                    label={Translation("Email")}
                    placeholder={Translation("Email")}
                    type="text"
                    leadingIcon={<EmailSvg />}
                    disabled={requested}
                  />
                  {requested ? (
                    <>
                      <span className="forgot-password__requested-span">
                        {Translation("Request sent, please check your inbox")}
                        <br />
                        {!resent && "Resending"}
                        {resent && (
                          <>
                            <span
                              style={{ textDecoration: "underline" }}
                              onClick={async () => {
                                setResent(false);
                                await createPasswordReset(values.email);
                                setResent(true);
                              }}
                            >
                              Resend
                            </span>{" "}
                            again
                          </>
                        )}
                      </span>
                    </>
                  ) : (
                    <Button
                      type="submit"
                      label={TranslationCommon("Confirm")}
                      trailingIcon={
                        isValid ? <ArrowRightSvg /> : <ArrowRightDisabledSvg />
                      }
                      style={{ margin: "20px 0 28px 0" }}
                      disabled={!isValid}
                    />
                  )}
                </Form>
              );
            }}
          </Formik>
          <div className="login__switch">
            <span>{Translation("Don't have an account yet?")}</span>
            <ButtonLink
              content={Translation("Sign Up")}
              onClick={handleReturn}
            />
          </div>
        </div>
      }
    />
  );
};

ForgotPassword.propTypes = {
  email: PropTypes.string,
  onClose: PropTypes.func,
  onReturn: PropTypes.func,
};

ForgotPassword.defaultProps = {
  email: "",
  onClose: () => {},
  onReturn: () => {},
};

export default ForgotPassword;
